/**
 * Bildirim Servisi
 *
 * 1. Browser Notification API — Pi beklerken şu an çalışır
 * 2. Email (EmailJS) — VITE_EMAILJS_* env değişkenleri ayarlanınca aktif olur
 *    EmailJS hesabı: https://www.emailjs.com (ücretsiz 200 mail/ay)
 *    Env dosyası (.env.local):
 *      VITE_EMAILJS_SERVICE_ID=service_xxx
 *      VITE_EMAILJS_TEMPLATE_ID=template_xxx
 *      VITE_EMAILJS_PUBLIC_KEY=xxxxx
 */

import type { AlertEvent, AlertEmailSettings, AlertNotificationSettings, SensorReading } from '../types';
import { supabase, SUPABASE_FUNCTIONS_URL } from './supabase';
import { saveAlert } from './dataSource';

export interface AlertContext {
  current?: SensorReading | null;
  healthScore?: number | null;
}

// ─── Browser Notifications ─────────────────────────────────────────────────

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function sendBrowserNotification(title: string, body: string, icon = '/mushroom.svg'): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, { body, icon, badge: icon, tag: 'gmc-alert' });
  } catch {}
}

// ─── EmailJS ───────────────────────────────────────────────────────────────

const EJ_SERVICE  = import.meta.env.VITE_EMAILJS_SERVICE_ID  as string | undefined;
const EJ_TEMPLATE = import.meta.env.VITE_EMAILJS_TEMPLATE_ID as string | undefined;
const EJ_KEY      = import.meta.env.VITE_EMAILJS_PUBLIC_KEY  as string | undefined;

export const emailConfigured = !!(EJ_SERVICE && EJ_TEMPLATE && EJ_KEY);

export async function sendEmailAlert(
  to: string,
  alert: AlertEvent,
): Promise<boolean> {
  if (!emailConfigured) return false;
  try {
    // Dynamic import — EmailJS yalnızca email gönderilecekse yüklenir
    const emailjs = await import('@emailjs/browser');
    await emailjs.send(
      EJ_SERVICE!,
      EJ_TEMPLATE!,
      {
        to_email:  to,
        severity:  alert.severity.toUpperCase(),
        metric:    alert.metric,
        value:     alert.value,
        threshold: alert.threshold,
        message:   alert.message,
        timestamp: new Date(alert.timestamp).toLocaleString('tr-TR'),
      },
      { publicKey: EJ_KEY! },
    );
    return true;
  } catch (e) {
    console.warn('[NotificationService] Email gönderilemedi:', e);
    return false;
  }
}

// ─── Daily Report ──────────────────────────────────────────────────────────

export interface DailyReportData {
  date: string;
  activeBagCount: number;
  healthScore: number;
  healthLabel: string;
  avgTemp: number;
  avgHumidity: number;
  avgCo2: number;
  alertCount: number;
  criticalAlertCount: number;
}

export async function sendDailyReport(to: string, data: DailyReportData): Promise<boolean> {
  if (!emailConfigured) {
    // Browser notification fallback
    sendBrowserNotification(
      '📊 Gün Sonu Raporu',
      `${data.date} — Sağlık: ${data.healthScore}/100 · Sıcaklık: ${data.avgTemp}°C · Nem: %${data.avgHumidity}`
    );
    return false;
  }
  try {
    const emailjs = await import('@emailjs/browser');
    const message = [
      `📊 GÜN SONU RAPORU — ${data.date}`,
      '',
      `🏠 Aktif Torbalar: ${data.activeBagCount}`,
      `💚 Sağlık Skoru: ${data.healthScore}/100 (${data.healthLabel})`,
      '',
      '🌡️ İklim Ortalamaları:',
      `  Sıcaklık: ${data.avgTemp}°C`,
      `  Nem: %${data.avgHumidity}`,
      `  CO₂: ${data.avgCo2} ppm`,
      '',
      `⚠️ Bugünkü Uyarılar: ${data.alertCount} (${data.criticalAlertCount} kritik)`,
    ].join('\n');

    await emailjs.send(
      EJ_SERVICE!,
      EJ_TEMPLATE!,
      {
        to_email:  to,
        severity:  'GÜN SONU RAPORU',
        metric:    'Tüm Sistemler',
        value:     `${data.healthScore}/100`,
        threshold: '80',
        message,
        timestamp: new Date().toLocaleString('tr-TR'),
      },
      { publicKey: EJ_KEY! },
    );
    sendBrowserNotification('📊 Gün Sonu Raporu Gönderildi', `${data.date} raporu ${to} adresine iletildi.`);
    return true;
  } catch (e) {
    console.warn('[NotificationService] Gün sonu raporu gönderilemedi:', e);
    return false;
  }
}

// ─── Telegram (Edge Function üzerinden — token frontend'de yok) ───────────

export const telegramConfigured = !!(SUPABASE_FUNCTIONS_URL);

export async function sendTelegramMessage(text: string): Promise<boolean> {
  if (!SUPABASE_FUNCTIONS_URL || !supabase) return false;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/send-telegram`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({ text }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

const METRIC_LABELS: Record<string, string> = {
  temperature: 'Sıcaklık', humidity: 'Nem', co2: 'CO₂', vpd: 'VPD', sensor: 'Sensör Bağlantısı',
};

const METRIC_UNITS: Record<string, string> = {
  temperature: '°C', humidity: '%', co2: 'ppm', vpd: 'kPa', sensor: 'dk',
};

const RECOMMENDED_ACTIONS: Record<string, Record<string, string>> = {
  co2: {
    critical: 'FAE fanını manuel aç. Çadır kapısını kısa süre havalandır. CO₂ 1000 ppm altına inene kadar izle.',
    warning:  'FAE fanının çalıştığını doğrula. Bir sonraki okumayı bekle.',
  },
  temperature: {
    critical: 'Isıtıcı/soğutucuyu kontrol et. Cihaz panelinden manuel müdahale yapabilirsin.',
    warning:  'Sıcaklık trendini izle. Ani değişim varsa ısıtıcı otomasyonunu gözden geçir.',
  },
  humidity: {
    critical: 'Nemlendiriciyi veya FAE dengesini kontrol et. Nem %85 altına düşmesin.',
    warning:  'Nemlendirici çalışıyor mu doğrula.',
  },
  sensor: {
    warning:  'HA Terminal → pgrep -f ha_to_supabase.py\nScript durmuşsa: bash /homeassistant/root/mantar_watchdog.sh',
  },
};

export async function sendTelegramAlert(alert: AlertEvent, ctx?: AlertContext): Promise<boolean> {
  const emoji = alert.severity === 'critical' ? '🚨' : alert.severity === 'warning' ? '⚠️' : 'ℹ️';
  const sevLabel = alert.severity === 'critical' ? 'KRİTİK' : alert.severity === 'warning' ? 'UYARI' : 'BİLGİ';
  const unit = METRIC_UNITS[alert.metric] ?? '';
  const metricLabel = METRIC_LABELS[alert.metric] ?? alert.metric;

  const lines: string[] = [
    `${emoji} <b>${sevLabel} — ${metricLabel}</b>`,
    '',
    `📈 Değer: <b>${alert.value}${unit}</b>  ·  Eşik: ${alert.threshold}${unit}`,
  ];

  // Anlık iklim bağlamı
  if (ctx?.current) {
    const c = ctx.current;
    lines.push(`🌡 Bağlam: ${c.temperature}°C · %${c.humidity} nem · ${c.co2} ppm CO₂`);
  }

  // Health score
  if (ctx?.healthScore != null) {
    const hs = ctx.healthScore;
    const hsEmoji = hs >= 85 ? '💚' : hs >= 70 ? '🟡' : '🔴';
    lines.push(`${hsEmoji} Sistem Sağlığı: ${hs}/100`);
  }

  lines.push('', `💬 ${alert.message}`);

  // Önerilen aksiyon
  const action = RECOMMENDED_ACTIONS[alert.metric]?.[alert.severity]
    ?? RECOMMENDED_ACTIONS[alert.metric]?.['warning'];
  if (action) {
    lines.push('', `🔧 <b>Önerilen:</b> ${action}`);
  }

  lines.push('', `🕐 ${new Date(alert.timestamp).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}`);

  return sendTelegramMessage(lines.join('\n'));
}

export async function sendTelegramDailyReport(data: DailyReportData): Promise<boolean> {
  const text = [
    `📊 <b>Gün Sonu Raporu — ${data.date}</b>`,
    '',
    `🏠 Aktif Torbalar: ${data.activeBagCount}`,
    `💚 Sağlık Skoru: ${data.healthScore}/100 (${data.healthLabel})`,
    '',
    `🌡 Sıcaklık: ${data.avgTemp}°C`,
    `💧 Nem: %${data.avgHumidity}`,
    `🌬 CO₂: ${data.avgCo2} ppm`,
    '',
    `⚠️ Uyarılar: ${data.alertCount} (${data.criticalAlertCount} kritik)`,
  ].join('\n');
  return sendTelegramMessage(text);
}

// ─── Sabah Aksiyon Bülteni ──────────────────────────────────────────────────

export interface MorningBulletinData {
  date: string;
  // Bugün yapılacaklar
  todayActions: Array<{
    bagId: string;
    bagCode: string;
    action: string;
    reason: string;
    priority: 'high' | 'medium' | 'low';
  }>;
  // Dikkat gerektiren durumlar
  warnings: Array<{
    bagId?: string;
    bagCode?: string;
    message: string;
  }>;
  // Dünün özeti
  yesterdayHarvestGrams: number;
  activeBagCount: number;
  lostBagCount: number;
  healthScore: number;
  // Cihaz durumları
  devices: Array<{ name: string; isActive: boolean }>;
}

export async function sendTelegramMorningBulletin(data: MorningBulletinData): Promise<boolean> {
  const dateStr = data.date;
  const lines: string[] = [];

  lines.push(`🌅 <b>GÜNAYIN — ${dateStr}</b>`);
  lines.push('');

  // Bugün yapılacaklar
  if (data.todayActions.length > 0) {
    lines.push('📋 <b>BUGÜN YAPILACAKLAR:</b>');
    for (const a of data.todayActions) {
      const emoji = a.priority === 'high' ? '🔴' : a.priority === 'medium' ? '🟡' : '🟢';
      lines.push(`${emoji} <b>${a.bagCode}</b> → ${a.action}`);
      lines.push(`   <i>${a.reason}</i>`);
    }
    lines.push('');
  } else {
    lines.push('📋 <b>BUGÜN YAPILACAKLAR:</b>');
    lines.push('✅ Özel aksiyon gerektiren torba yok');
    lines.push('');
  }

  // Uyarılar
  if (data.warnings.length > 0) {
    lines.push('⚠️ <b>DİKKAT:</b>');
    for (const w of data.warnings) {
      const prefix = w.bagCode ? `${w.bagCode}: ` : '';
      lines.push(`• ${prefix}${w.message}`);
    }
    lines.push('');
  }

  // Dünün özeti
  lines.push('📊 <b>DÜNÜN ÖZETİ:</b>');
  lines.push(`• Hasat: ${data.yesterdayHarvestGrams}g | Aktif bag: ${data.activeBagCount} | Kaybedilen: ${data.lostBagCount}`);
  lines.push(`• Sağlık skoru: ${data.healthScore}/100`);
  lines.push('');

  // Cihaz durumları
  if (data.devices.length > 0) {
    const deviceStr = data.devices
      .map(d => `${d.name} ${d.isActive ? '✅' : '❌'}`)
      .join(' | ');
    lines.push(`🔋 <b>CİHAZLAR:</b> ${deviceStr}`);
  }

  return sendTelegramMessage(lines.join('\n'));
}

// ─── Gece modu (quiet hours) kontrolü ──────────────────────────────────────

function isQuietHour(notifSettings?: AlertNotificationSettings, severity?: string): boolean {
  const qh = notifSettings?.quietHours;
  if (!qh?.enabled) return false;
  // Kritik alarmlar gece modunu delebilinir
  if (qh.allowCritical && severity === 'critical') return false;
  const hour = new Date().getHours();
  const { startHour, endHour } = qh;
  // Gece yarısını geçen aralık (örn. 23:00 – 06:00)
  return startHour > endHour
    ? hour >= startHour || hour < endHour
    : hour >= startHour && hour < endHour;
}

// ─── Unified dispatcher ─────────────────────────────────────────────────────

export function dispatchAlert(alert: AlertEvent, emailSettings: AlertEmailSettings, notifSettings?: AlertNotificationSettings, ctx?: AlertContext): void {
  // Persist to Supabase (skip ephemeral sensor-watchdog entries)
  if (alert.metric !== 'sensor') {
    saveAlert(alert);
  }

  const sev = alert.severity;
  const severityEmoji = sev === 'critical' ? '🚨' : sev === 'warning' ? '⚠️' : 'ℹ️';
  const metricLabels: Record<string, string> = {
    temperature: 'Sıcaklık', humidity: 'Nem', co2: 'CO₂',
  };

  const quiet = isQuietHour(notifSettings, sev);

  // Browser notification — susturulamaz kritikler + gece modu kontrolü
  const browserEnabled = notifSettings
    ? notifSettings.browser.enabled && notifSettings.browser.severities[sev]
    : true;
  if (browserEnabled && !quiet) {
    sendBrowserNotification(
      `${severityEmoji} Çadır Uyarısı — ${metricLabels[alert.metric] ?? alert.metric}`,
      alert.message,
    );
  }

  // Email — gece modu: sessiz (kritik izin varsa geç)
  if (
    emailSettings.enabled &&
    emailSettings.email &&
    emailSettings.severities[sev] &&
    !quiet
  ) {
    sendEmailAlert(emailSettings.email, alert);
  }

  // Telegram — gece modu kontrolü
  const telegramEnabled = notifSettings ? notifSettings.telegram.enabled && notifSettings.telegram.severities[sev] : false;
  if (telegramConfigured && telegramEnabled && !quiet) {
    // Supabase Edge Functions (notify-threshold & sensor-watchdog) kendi Telegram mesajlarını atıyor.
    // Frontend'in tekrar atmasını engelle ki mükerrer veya farklı formatta mesaj gitmesin.
    const isHandledByEdgeFunction = alert.source === 'server-threshold' || alert.metric === 'sensor';
    if (!isHandledByEdgeFunction) {
      sendTelegramAlert(alert, ctx);
    }
  }
}
